import React from 'react';
import { FiX, FiPaperclip, FiSend } from 'react-icons/fi';
import { Sheet } from '../../../../../components/Sheet/Sheet';
import * as translator from '../../../../../utils/translator';
import logo_v2025 from '../../../../../images/logo_v2025.png';
import ConversationAttachment from '../../ConversationAttachment/ConversationAttachment';
import { useDoctorChat, ChatChannel } from '../hooks/useDoctorChat';
import {
	CONVERSATION_ATTACHMENT_ACCEPT,
	ConversationFileData,
	readFilesAsAttachmentData,
} from '../../../utils/readFilesAsAttachmentData';

interface ChatWithDoctorSheetProps {
	isOpen: boolean;
	onClose: () => void;
	country: string;
	language: string;
	channel: ChatChannel;
	role?: string;
}

/** Header label per thread, so the patient can tell which chat they are in. */
const titleKeyByChannel: Record<ChatChannel, string> = {
	doctor: 'CareProfile/Chat/TitleDoctor',
	nutritionist: 'CareProfile/Chat/TitleNutritionist',
	support: 'CareProfile/Chat/TitleSupport',
};

const emptyKeyByChannel: Record<ChatChannel, string> = {
	doctor: 'CareProfile/Chat/Empty',
	nutritionist: 'CareProfile/Chat/EmptyNutritionist',
	support: 'CareProfile/Chat/EmptySupport',
};

const ChatWithDoctorSheet: React.FC<ChatWithDoctorSheetProps> = ({ isOpen, onClose, country, language, channel, role }) => {
	const { messages, loading, sending, error, load, send } = useDoctorChat({ country, language, channel, role });
	const [inputText, setInputText] = React.useState('');
	const [files, setFiles] = React.useState<ConversationFileData[]>([]);
	const [fileError, setFileError] = React.useState<string | null>(null);
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	const scrollRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (isOpen) {
			setInputText('');
			setFiles([]);
			setFileError(null);
			load();
		}
	}, [isOpen]);

	React.useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		}
	}, [messages, loading]);

	const handleAttachClick = () => {
		fileInputRef.current?.click();
	};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const selected = event.target.files;
		if (!selected?.length) {
			return;
		}

		const { files: accepted, rejected } = await readFilesAsAttachmentData(selected, {
			existingCount: files.length,
		});

		if (accepted.length) {
			setFiles((prev) => [...prev, ...accepted]);
		}

		if (rejected.includes('type')) {
			setFileError(translator.translate(language, 'my_account', 'CareProfile/Chat/FileTypeNotAllowed') || 'File type not allowed');
		} else if (rejected.includes('size')) {
			setFileError(translator.translate(language, 'my_account', 'CareProfile/Chat/FileTooLarge') || 'File exceeds 5 MB limit');
		} else if (rejected.includes('count')) {
			setFileError(translator.translate(language, 'my_account', 'CareProfile/Chat/TooManyFiles') || 'Maximum 5 files');
		} else {
			setFileError(null);
		}

		// Allow re-selecting the same file path after remove.
		event.target.value = '';
	};

	const removeFile = (index: number) => {
		setFiles((prev) => prev.filter((_, i) => i !== index));
		setFileError(null);
	};

	const handleSend = async () => {
		if (!inputText.trim() || sending) {
			return;
		}

		const text = inputText;
		const pendingFiles = files;
		const ok = await send(text, pendingFiles);
		if (ok) {
			setInputText('');
			setFiles([]);
			setFileError(null);
		}
	};

	return (
		<Sheet isOpen={isOpen} onClose={onClose}>
			<div id="care-profile-chat" className="flex flex-col w-full h-[70dvh] lg:h-[600px]">
				{/* Header */}
				<div className="relative flex items-center justify-center bg-[#f5f6f7] px-3 py-3.5 flex-shrink-0">
					<div className="flex flex-col items-center gap-y-0.5">
						<img src={logo_v2025} alt="DoktorABC" className="h-[21px] w-auto" />
						<p className="text-[12px] font-medium text-care-plus-heading">
							{translator.translate(language, 'my_account', titleKeyByChannel[channel])}
						</p>
					</div>
					<button
						id="care-profile-chat-close"
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="absolute right-2 flex items-center justify-center w-8 h-8 rounded-full border-none bg-transparent text-care-plus-heading cursor-pointer"
					>
						<FiX size={19} />
					</button>
				</div>

				{/* Messages */}
				<div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-y-3">
					{loading && messages.length === 0 ? (
						<p className="text-center text-[13px] text-[#99a3b2] mt-6">{translator.translate(language, 'my_account', 'CareProfile/Chat/Loading')}</p>
					) : messages.length === 0 ? (
						<p className="text-center text-[13px] text-[#99a3b2] mt-6">{translator.translate(language, 'my_account', emptyKeyByChannel[channel])}</p>
					) : (
						messages.map((message, index) => {
							const isDoctor = message.user_type?.includes('doctor');
							const attachments = message.attachments ?? [];
							return (
								<div key={index} className={`flex flex-col max-w-[85%] ${isDoctor ? 'items-start self-start' : 'items-end self-end'}`}>
									<div
										className={`px-3 py-2.5 rounded-[6px] text-[15px] leading-[21px] text-care-plus-heading font-roboto break-words ${
											isDoctor ? 'bg-[#fafcfc] border border-solid border-[#f5f6f7]' : 'bg-[#edf9fc]'
										}`}
									>
										<div dangerouslySetInnerHTML={{ __html: message.content }} />
										{attachments.length > 0 && (
											<div className="message-content">
												{attachments.map((attachment: any, attachmentIndex: number) => (
													<ConversationAttachment key={attachment.attachment_id ?? attachmentIndex} attachment={attachment} />
												))}
											</div>
										)}
									</div>
									{message.post_time && <span className="text-[10px] text-[#99a3b2] mt-1 px-1">{message.post_time}</span>}
								</div>
							);
						})
					)}
				</div>

				{/* Pending files + errors */}
				{(files.length > 0 || fileError || error) && (
					<div className="flex-shrink-0 px-2.5 pb-1 flex flex-col gap-y-1.5">
						{fileError && <p className="text-[12px] text-red-600 m-0 px-1">{fileError}</p>}
						{error && (
							<p className="text-[12px] text-red-600 m-0 px-1">
								{translator.translate(language, 'my_account', 'CareProfile/Chat/SendError') || 'Could not send message. Try again.'}
							</p>
						)}
						{files.length > 0 && (
							<div className="flex flex-wrap gap-1.5">
								{files.map((file, index) => (
									<button
										key={`${file.filename}-${index}`}
										type="button"
										onClick={() => removeFile(index)}
										className="flex items-center gap-x-1 max-w-full bg-white border border-solid border-[#d9dee7] rounded-[6px] px-2 py-1 text-[12px] text-care-plus-heading cursor-pointer"
										aria-label={`Remove ${file.filename}`}
									>
										<span className="truncate max-w-[160px]">{file.filename}</span>
										<FiX size={12} className="flex-shrink-0 text-[#99a3b2]" />
									</button>
								))}
							</div>
						)}
					</div>
				)}

				{/* Input bar */}
				<div className="flex-shrink-0 px-2.5 pb-3 pt-1">
					<div className="flex items-center gap-x-2 bg-[#f5f6f7] rounded-[7px] px-3 py-2.5">
						<button
							id="care-profile-chat-attach"
							type="button"
							onClick={handleAttachClick}
							aria-label="Attach"
							className="flex items-center justify-center text-[#99a3b2] border-none bg-transparent flex-shrink-0 cursor-pointer"
						>
							<FiPaperclip size={20} />
						</button>
						<input
							ref={fileInputRef}
							id="care-profile-chat-file-input"
							type="file"
							multiple
							accept={CONVERSATION_ATTACHMENT_ACCEPT}
							onChange={handleFileChange}
							className="hidden"
						/>
						<input
							id="care-profile-chat-input"
							type="text"
							value={inputText}
							onChange={(event) => setInputText(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === 'Enter') {
									event.preventDefault();
									handleSend();
								}
							}}
							placeholder={translator.translate(language, 'my_account', 'CareProfile/Chat/InputPlaceholder')}
							className="flex-1 bg-transparent border-none outline-none text-[15px] text-care-plus-heading placeholder:text-[#99a3b2] font-roboto min-w-0"
						/>
						<button
							id="care-profile-chat-send"
							type="button"
							onClick={handleSend}
							disabled={sending || !inputText.trim()}
							aria-label="Send"
							className="flex items-center justify-center w-[30px] h-[30px] rounded-full bg-[#269c6e] text-white border-none flex-shrink-0 disabled:opacity-50 cursor-pointer"
						>
							<FiSend size={15} />
						</button>
					</div>
				</div>
			</div>
		</Sheet>
	);
};

export default ChatWithDoctorSheet;
